import {
    useEffect,
    useLayoutEffect,
    useRef,
    type ReactNode,
    type MouseEvent as ReactMouseEvent,
} from "react";
import {useLocation} from "react-router-dom";
import classes from "./Dialog.module.css";

type DialogProps = {
    children: ReactNode;
    active: boolean;
    escapeExits?: boolean;
    clickOutsideExits?: boolean;
    onClose?: () => void;
    className?: string;
    "data-pw"?: string;
};

const clickedWithin = (
    rect: {x: number; y: number; width: number; height: number},
    x: number,
    y: number,
) =>
    x >= rect.x &&
    x <= rect.x + rect.width &&
    y >= rect.y &&
    y <= rect.y + rect.height;

export const Dialog = ({
    children,
    active,
    escapeExits = true,
    clickOutsideExits = false,
    onClose,
    className,
    ...rest
}: DialogProps) => {
    const location = useLocation();
    const prevLocation = useRef(location);
    const dialogRef = useRef<HTMLDialogElement>(null);

    const tryClose = () => {
        if (dialogRef.current?.open) {
            dialogRef.current.close();
        }
        onClose?.();
    };

    useLayoutEffect(() => {
        const el = dialogRef.current;
        if (!el) return;
        if (active && !el.open) {
            el.showModal();
        } else if (!active && el.open) {
            el.close();
        }
    }, [active]);

    useEffect(() => {
        const listener = (event: globalThis.KeyboardEvent) => {
            if (event.key !== "Escape") return;
            if (!escapeExits) {
                event.preventDefault();
            } else {
                onClose?.();
            }
        };
        document.addEventListener("keydown", listener);
        return () => document.removeEventListener("keydown", listener);
    }, [escapeExits, onClose]);

    useEffect(() => {
        if (prevLocation.current !== location) {
            prevLocation.current = location;
            tryClose();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);

    const handleClick = (e: ReactMouseEvent<HTMLDialogElement>) => {
        if ((e.target as HTMLElement).tagName !== "DIALOG") return;
        if (!clickOutsideExits) return;
        const rect = (e.target as HTMLDialogElement).getBoundingClientRect();
        if (!clickedWithin(rect, e.clientX, e.clientY)) {
            tryClose();
        }
    };

    return (
        <dialog
            ref={dialogRef}
            className={[classes.dialog, className].filter(Boolean).join(" ")}
            onClick={handleClick}
            {...rest}
        >
            {active && children}
        </dialog>
    );
};
